/**
 * Phase B — `IdeaWorkspace.tsx` 用の helper 関数 / 定数。
 *
 * 旧 `old/apps/user/src/components/ecw/IdeaForm.tsx` 内に inline されていた
 *   - getDisplayName (nickname + ageGroup/gender 表示)
 *   - formatPostsAsFacts ({{FACTS}} 用文字列)
 *   - buildQuestionsBlock / buildReferencesBlock (aiReferences → {{QUESTIONS}} / {{REFERENCES}})
 *   - colorPalette (back モード mini-map の 8 色)
 *   - sentiment 計算 (selectedPosts の HappinessScore 平均 / 3 を -1..1 clip)
 * を共通モジュールに切り出して、IdeaWorkspace 本体を見やすくする。
 *
 * 注: shared-types EcwInsight / EcwIdea の full schema 駆動 (Phase A で拡張済)。
 */

import {
  POST_TYPE_FIELD_KEY,
  type AiCategoryMasterRow,
  type AiDialogueFactsConfig,
  type AiDialogueFactsFieldSet,
  type AiReference,
  type FieldDef,
} from '@citizen-walk/shared-types';
import { getPostCategoryCodes, type PostMinimal } from '../posts';

/** 旧 IdeaForm L554 踏襲: back モード mini-map で insight ごとに振る 8 色パレット。 */
export const COLOR_PALETTE = [
  '#e74c3c',
  '#3498db',
  '#f1c40f',
  '#2ecc71',
  '#9b59b6',
  '#e67e22',
  '#1abc9c',
  '#34495e',
] as const;

export interface DisplayNameInput {
  nickname: string;
  ageGroup?: string;
  gender?: string;
}

/** 旧 IdeaForm `getDisplayName` 等価。`${nickname}（${ageGroup}/${gender}）` 形式。 */
export function formatDisplayName(input: DisplayNameInput): string {
  const parts: string[] = [];
  if (input.ageGroup) parts.push(input.ageGroup);
  if (input.gender) parts.push(input.gender);
  if (parts.length > 0) {
    return `${input.nickname}（${parts.join('/')}）`;
  }
  return input.nickname;
}

/** unknown (index signature 由来の動的 field) を trim 済 string に narrow するヘルパー。 */
function asTrimmedString(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

function formatLatLng(p: PostMinimal): string {
  return p.location
    ? `${p.location.latitude.toFixed(6)},${p.location.longitude.toFixed(6)}`
    : '（位置情報なし）';
}

/**
 * `postFields` 未指定時に使う組込項目の fallback 定義。
 *
 * 地区の `dataSettings.postFields` を渡せない呼出 (単体テスト / 地区未解決時) でも
 * タイトル・位置・コメントは従来どおり {{FACTS}} に載るようにするための最小セット。
 */
const DEFAULT_FACT_FIELDS: FieldDef[] = [
  {
    key: 'title',
    label: 'タイトル',
    type: 'text',
    mode: 'required',
    options: [],
    builtIn: true,
    description: '',
    definition: '',
  },
  {
    key: 'location',
    label: '位置',
    type: 'location',
    mode: 'required',
    options: [],
    builtIn: true,
    description: '',
    definition: '',
  },
  {
    key: 'comment',
    label: 'コメント',
    type: 'textarea',
    mode: 'optional',
    options: [],
    builtIn: true,
    description: '',
    definition: '',
  },
];

/**
 * `PostMinimal` 直下に実体があり `extraFields` には入らない組込 field key。
 * `postFields` に含まれる場合は本体側の値を優先し、空なら extraFields も見る。
 */
const BODY_FIELD_KEYS: ReadonlySet<string> = new Set(['title', 'comment']);

/** 「値あり」判定 (PostDetailFields の hasValue と同方針、未入力項目を {{FACTS}} に出さないため)。 */
function hasFactValue(field: FieldDef, value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return false;
    if (field.type === 'rating-scale' && value === 0 && field.optionsMeta?.excludeZero === true) {
      return false;
    }
    return true;
  }
  if (typeof value === 'boolean') return value === true;
  return Boolean(value);
}

/** select / multiselect の value → label を解決 (見つからなければ value のまま)。 */
function factOptionLabel(field: FieldDef, value: unknown): string {
  const v = String(value);
  return field.options?.find((o) => o.value === v)?.label ?? v;
}

/** 1 行 1 項目の list に載せるため、改行を空白に畳んだ 1 行文字列にする。 */
function toSingleLine(v: unknown): string {
  return String(v)
    .replace(/\s*\r?\n\s*/g, ' ')
    .trim();
}

/** 投稿者が入力した値を型別に人間可読な文字列へ整形する (PostDetailFields の読取専用描画と同方針)。 */
function formatFactValue(field: FieldDef, value: unknown): string {
  switch (field.type) {
    case 'select':
      return factOptionLabel(field, value);
    case 'multiselect': {
      const arr = Array.isArray(value) ? (value as unknown[]) : [value];
      return arr.map((v) => factOptionLabel(field, v)).join('、');
    }
    case 'rating-scale': {
      const n = Number(value);
      if (!Number.isFinite(n)) return toSingleLine(value);
      return n > 0 ? `+${n}` : String(n);
    }
    case 'checkbox':
      return 'はい';
    default:
      return toSingleLine(value);
  }
}

/** `postFields` の 1 項目を投稿から読み出す (本体 field → extraFields の順に見る)。 */
function readPostFieldValue(p: PostMinimal, key: string): unknown {
  if (BODY_FIELD_KEYS.has(key)) {
    const body = key === 'title' ? p.title : p.comment;
    if (typeof body === 'string' && body.trim() !== '') return body;
  }
  return p.extraFields?.[key];
}

/** {{FACTS}} に載せる項目を displayOrder 昇順で返す (入力フォームに出ない hidden は除外)。 */
function orderedFactFields(fields: FieldDef[]): FieldDef[] {
  return fields
    .filter((f) => f.mode !== 'hidden')
    .map((f, idx) => ({ f, order: f.displayOrder ?? Number.MAX_SAFE_INTEGER, idx }))
    .sort((a, b) => a.order - b.order || a.idx - b.idx)
    .map((x) => x.f);
}

/**
 * 投稿 1 件に紐づく AI 分類コードを重複なしで集める。
 *
 * - `getPostCategoryCodes` が ECW (`categoryCodes`) / 防災・ウォーカブル
 *   (`classifications[].category_code`) / generic の `classifications[].*_code` を回収する。
 * - fields[] 駆動 (列名動的化レベル 3) の generic はフラット出力なので、`category_codes` のような
 *   top-level の `*_code` / `*_codes` も拾う (配列 / カンマ区切り文字列の両方に対応)。
 */
function collectPostCategoryCodes(p: PostMinimal): string[] {
  const out: string[] = [];
  const push = (v: unknown) => {
    if (typeof v !== 'string') return;
    for (const part of v.split(',')) {
      const code = part.trim();
      if (code) out.push(code);
    }
  };
  for (const c of getPostCategoryCodes(p)) push(c);
  const generic = p.aiAnalysisGeneric;
  if (generic && typeof generic === 'object' && !Array.isArray(generic)) {
    for (const [k, v] of Object.entries(generic)) {
      if (!k.endsWith('_code') && !k.endsWith('_codes')) continue;
      if (Array.isArray(v)) v.forEach(push);
      else push(v);
    }
  }
  return [...new Set(out)];
}

/**
 * AI 解析結果のテキスト項目 (`aiTextFields`) を FACTS に出すときの日本語ラベル。
 * 未知のキー (fields[] 駆動 generic の地区固有列など) はキー名をそのまま使う。
 */
const AI_TEXT_FIELD_LABELS: Readonly<Record<string, string>> = {
  description: 'AI の状況説明',
  professional_insight: 'AI の所見',
  reasoning: 'AI の判断根拠',
  emotionText: 'AI が読み取った感情',
  emotionReason: 'AI の感情判断の理由',
  categoryReason: 'AI の分類理由',
  notice: 'AI が気づいたこと',
  bousai_resource: 'AI が見つけた防災資源',
};

/** `aiTextFields` の値を 1 行の文字列にする (文字列 / 数値 / 文字列配列に対応、それ以外は空)。 */
function stringifyAiTextValue(v: unknown): string {
  if (typeof v === 'string') return toSingleLine(v);
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  if (Array.isArray(v)) {
    return v
      .map((x) => (typeof x === 'string' || typeof x === 'number' ? toSingleLine(x) : ''))
      .filter((s) => s !== '')
      .join('、');
  }
  return '';
}

/**
 * 指定キーの AI 解析テキストを投稿から読み出す。
 *
 * 取得元はモードごとに異なる (防災 = `aiAnalysisBousai`、ウォーカブル = `aiAnalysisWalker`、
 * ECW = `aiAnalysisEcw`、fields[] 駆動 = `aiAnalysisGeneric`) ため、**すべて**を順に探して
 * 最初に見つかった非空の値を使う。
 */
function readAiTextField(p: PostMinimal, key: string): string {
  const sources: unknown[] = [
    p.aiAnalysisBousai,
    p.aiAnalysisWalker,
    p.aiAnalysisEcw,
    p.aiAnalysisGeneric,
  ];
  for (const src of sources) {
    if (!src || typeof src !== 'object' || Array.isArray(src)) continue;
    const text = stringifyAiTextValue((src as Record<string, unknown>)[key]);
    if (text) return text;
  }
  return '';
}

/** 対話設定を解決した後の、実際に使う項目構成 (未設定 key は既定で埋まっている)。 */
interface ResolvedFactsFieldSet {
  postFields: boolean | string[];
  aiGeneratedTitle: boolean;
  categoryCodes: boolean;
  categoryMaster: boolean;
  aiTextFields: string[];
}

/**
 * 対話設定 (`AiDialogueSettings.factsConfig`) と選択件数から、実際に使う項目構成を決める。
 *
 * - `factsConfig` 未設定 (undefined / null) → 既定 = 投稿者入力項目すべて + AI 生成タイトル +
 *   AI 分類コード + 分類コード対応表 (AI の所見テキストは含めない) = 従来挙動。
 * - `largeSelection.thresholdPosts` を**超える**件数が選択されている → 縮小構成に切替
 *   (`reduced=true`)。呼び出し側は FACTS 先頭に注記を添える。
 */
function resolveFactsFieldSet(
  config: AiDialogueFactsConfig | null | undefined,
  postCount: number,
): { fieldSet: ResolvedFactsFieldSet; reduced: boolean } {
  const large = config?.largeSelection ?? null;
  const threshold = large ? Number(large.thresholdPosts) : Number.NaN;
  const reduced = large !== null && Number.isFinite(threshold) && postCount > threshold;
  const src: AiDialogueFactsFieldSet = (reduced ? large : config) ?? {};
  return {
    reduced,
    fieldSet: {
      postFields: src.postFields ?? true,
      aiGeneratedTitle: src.aiGeneratedTitle ?? true,
      categoryCodes: src.categoryCodes ?? true,
      categoryMaster: src.categoryMaster ?? true,
      aiTextFields: (src.aiTextFields ?? []).map((k) => k.trim()).filter((k) => k !== ''),
    },
  };
}

/** `postFields` 設定 (true = 全部 / string[] = 指定キーのみ / false = 出さない) を適用する。 */
function selectFactFields(
  postFields: FieldDef[] | undefined,
  spec: boolean | string[],
): FieldDef[] {
  if (spec === false) return [];
  const ordered = orderedFactFields(
    postFields && postFields.length > 0 ? postFields : DEFAULT_FACT_FIELDS,
  );
  if (!Array.isArray(spec)) return ordered;
  const allow = new Set(spec.map((k) => k.trim()).filter((k) => k !== ''));
  return ordered.filter((f) => allow.has(f.key));
}

/**
 * {{FACTS}} 用文字列 (モード非依存の単一実装、2026-08-31 拡充 → 対話設定で構成可に)。
 *
 * 既定 (`factsConfig` 未指定 / 未設定) で投入する内容は 3 種類:
 *   1. 投稿者が入力した項目すべて — 地区の `dataSettings.postFields` 駆動。ラベル (`label`) と
 *      型 (select の選択肢ラベル / rating-scale の符号付き数値等) を解決した人間可読形にする。
 *      未入力の項目は出さない。写真は文字列化できないので出さない。
 *   2. AI 生成タイトル (`aiAnalysisGeneratedTitle`)。
 *   3. AI の対象分類の **コードのみ**。コードの意味は {{CATEGORY_MASTER}}
 *      (`buildCategoryMasterBlock`) 側の対応表で参照させる。
 *
 * 分類の**理由** (`categoryReason` / `emotionReason` / classifications[].comment 等) や AI の
 * 自由記述 (bousai の `description` / `professional_insight` 等) は長文で FACTS を埋めるため
 * 既定では含めない。必要な地区は M4 対話設定の `factsConfig.aiTextFields` にキーを列挙して
 * 明示的に追加する (管理画面「AI に渡す投稿データ」)。
 *
 * `factsConfig.largeSelection` が設定され、選択件数がその閾値を超えるときは縮小構成に切り替え、
 * 情報が少ないことを AI が誤解しないよう FACTS 先頭に注記を 1 行入れる。
 *
 * `postFields` 省略時 (または空配列) は `DEFAULT_FACT_FIELDS` (タイトル / 位置 / コメント) で
 * 従来どおり動作する。
 */
export function formatPostsAsFacts(
  posts: PostMinimal[],
  postFields?: FieldDef[],
  factsConfig?: AiDialogueFactsConfig | null,
): string {
  const { fieldSet, reduced } = resolveFactsFieldSet(factsConfig, posts.length);
  const fields = selectFactFields(postFields, fieldSet.postFields);
  const body = posts
    .map((p, i) => {
      const lines = [`- 投稿${i + 1}`];
      for (const f of fields) {
        // 写真は文字列化できないため出さない (存在有無も AI の判断材料にならない)。
        if (f.type === 'photo' || f.key === 'photo') continue;
        if (f.type === 'location' || f.key === 'location') {
          if (p.location) lines.push(`  ${f.label}: ${formatLatLng(p)}`);
          continue;
        }
        const raw = readPostFieldValue(p, f.key);
        if (!hasFactValue(f, raw)) continue;
        const text = formatFactValue(f, raw);
        if (text) lines.push(`  ${f.label}: ${text}`);
      }
      if (fieldSet.aiGeneratedTitle) {
        const generatedTitle = asTrimmedString(p.aiAnalysisGeneratedTitle);
        if (generatedTitle) lines.push(`  AI 生成タイトル: ${generatedTitle}`);
      }
      if (fieldSet.categoryCodes) {
        const codes = collectPostCategoryCodes(p);
        if (codes.length > 0) lines.push(`  AI 分類コード: ${codes.join(', ')}`);
      }
      for (const key of fieldSet.aiTextFields) {
        const text = readAiTextField(p, key);
        if (text) lines.push(`  ${AI_TEXT_FIELD_LABELS[key] ?? key}: ${text}`);
      }
      return lines.join('\n');
    })
    .join('\n');
  if (!reduced) return body;
  // 縮小が発動したことを AI に明示 (情報が少ないことを「材料不足」と誤解させないため)。
  const note = `※ 選択された投稿が ${posts.length} 件と多いため、各投稿は主要な項目のみを掲載しています。`;
  return body ? `${note}\n${body}` : note;
}

/**
 * {{CATEGORY_MASTER}} 用文字列 — **選択中の投稿に実際に出現した分類コードだけ**の対応表。
 *
 * 分類マスタ (M2) は 1 軸で 80 行を超えることもあり全件を毎回 AI に送るのは無駄なため、
 * `formatPostsAsFacts` が載せたコードの意味を引けるだけの最小限に絞る。
 * 1 行の形式は `- <コード>: <大分類> / <カテゴリ名>` (大分類が無い行は `- <コード>: <カテゴリ名>`)。
 *
 * `byCategoryCd` は `useAiCategoryMasters(settingsId).byCategoryCd` をそのまま渡す
 * (bare `categoryCd` と `majorCd-categoryCd` の結合キーの両方が登録済)。マスタに無いコードは
 * 説明できないので行を作らない。マスタ未読込 (空 map) の場合は空文字を返し、
 * サーバ側で {{CATEGORY_MASTER}} が空に置換される。
 *
 * `factsConfig` (M4 対話設定) で対応表が off のとき、および大量選択で縮小構成に切り替わり
 * その構成で off のときは空文字を返す (`formatPostsAsFacts` と同じ判定を共有)。
 */
export function buildCategoryMasterBlock(
  posts: PostMinimal[],
  byCategoryCd: ReadonlyMap<string, AiCategoryMasterRow> | null | undefined,
  factsConfig?: AiDialogueFactsConfig | null,
): string {
  if (!resolveFactsFieldSet(factsConfig, posts.length).fieldSet.categoryMaster) return '';
  if (!byCategoryCd || byCategoryCd.size === 0) return '';
  const seen = new Set<string>();
  for (const p of posts) {
    for (const code of collectPostCategoryCodes(p)) seen.add(code);
  }
  const lines: string[] = [];
  for (const code of [...seen].sort()) {
    const row = byCategoryCd.get(code);
    if (!row) continue;
    const major = (row.major ?? '').trim();
    const name = (row.category ?? '').trim();
    const desc = major ? (name ? `${major} / ${name}` : major) : name;
    if (!desc) continue;
    lines.push(`- ${code}: ${desc}`);
  }
  return lines.join('\n');
}

/**
 * 選択投稿群から AI 対話の文脈ラベル ({{EMOTION}} / {{CATEGORY}}) を導出する。
 *
 * - ECW: 先頭 ECW 投稿の `emotions[0]`(プルチック感情) / `categoryCodes[0]`。
 *   未解析 fallback は ECW 固有の '喜び'。
 * - 防災 (kids-bousai/bousai): 投稿の投稿タイプ (`extraFields.postType`)(ホッと/ハッと/ヒント) を
 *   {{EMOTION}} ラベルに、bousai 解析の先頭 `category_name`(見つけたもの) を {{CATEGORY}} に。
 *   '喜び' のような ECW 固有 fallback は使わない (空文字)。
 *
 * AnalysisPage の handleStartFrontWorkspace / handleStartBackWorkspace が呼ぶ。
 * ProceedContext (分析詳細タブからの emotion/categoryLabel) は呼び出し側で優先する。
 */
export function deriveDialogueContext(posts: PostMinimal[]): {
  emotion: string;
  categoryLabel: string;
} {
  const ecw = posts.find((p) => p.aiAnalysisEcw);
  if (ecw?.aiAnalysisEcw) {
    return {
      emotion: ecw.aiAnalysisEcw.emotions[0] || '喜び',
      categoryLabel: ecw.aiAnalysisEcw.categoryCodes[0] || '',
    };
  }
  const bousai = posts.find(
    (p) => p.aiAnalysisBousai || asTrimmedString(p.extraFields?.[POST_TYPE_FIELD_KEY]),
  );
  if (bousai) {
    const label = asTrimmedString(bousai.extraFields?.[POST_TYPE_FIELD_KEY]);
    const cls = bousai.aiAnalysisBousai?.classifications?.[0];
    const assessment = cls ? asTrimmedString(cls.assessment) : '';
    const categoryName = cls ? asTrimmedString(cls.category_name) : '';
    return { emotion: label || assessment, categoryLabel: categoryName };
  }
  return { emotion: '', categoryLabel: '' };
}

/**
 * 旧 IdeaForm の `questionsString` 等価 ({{QUESTIONS}} 用文字列)。
 *
 * 旧 v1 では questions.json (`EcwQuestionData[]`) から「問いのタイプ + 問い1」を抽出していた。
 * v2 では `aiReferences` collection (Sprint BG-DataSettings 24) を流用し、
 * `postExample` がある行を「問いのタイプ」相当として扱う簡易版。
 *
 * Phase B では `references[].postExample` を fallback の問い source とする
 * (旧の questions.json の挙動と完全一致しないが、aiReferences で十分カバーされる)。
 */
export function buildQuestionsBlock(refs: AiReference[]): string {
  return refs
    .filter((r) => r.postExample || r.title)
    .map((r) => `- [${r.factorType ?? '一般'}] ${r.postExample || r.title}`)
    .join('\n');
}

/**
 * 旧 IdeaForm の `referencesString` 等価 ({{REFERENCES}} 用文字列、step3 用)。
 *
 * 旧 v1: `references.json` から `参考となる情報・アイデアの例` + `情報・アイデアの内容や効果`
 * v2: aiReferences の `title` + `body`
 */
export function buildReferencesBlock(refs: AiReference[]): string {
  return refs.map((r) => `- ${r.title}: ${r.body}`).join('\n');
}

/**
 * 旧 IdeaForm `handleSaveInsight` 内の sentiment 計算式を切り出し。
 *
 * `HappinessScore` (旧: extraFields の任意 number、-3..+3 想定) の平均を 3 で割り、
 * -1..+1 に clip する。旧 v1 では `getEcwHappinessScore(p)` で extraFields 取得していたが、
 * v2 では `PostMinimal.extraFields` を直接参照する。
 */
export function computeAverageSentiment(posts: PostMinimal[]): number {
  if (posts.length === 0) return 0;
  let sum = 0;
  let n = 0;
  for (const p of posts) {
    const raw = p.extraFields?.HappinessScore;
    const v = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isFinite(v)) {
      sum += v;
      n += 1;
    }
  }
  if (n === 0) return 0;
  const avg = sum / n;
  const normalized = avg / 3;
  if (normalized < -1) return -1;
  if (normalized > 1) return 1;
  return normalized;
}

/**
 * 複合 `actionIdea` を「処方・アクション」と「効能」に分割する (docs/107 Wave8b)。
 *
 * 旧 v1 / old2 IdeaCardList L791 / L809 踏襲。`actionIdea` は AI 対話 Step4/5 を経て
 * `【処方・アクション】\n${action}\n\n【効能】\n${effect}` 形式で 1 field に複合化されている
 * (shared-types `EcwIdea.actionIdea` doc comment 参照)。
 *
 *   - `prescription` = `【効能】` より前を取り、`【処方・アクション】` ラベルを除去して trim
 *   - `effect`       = `【効能】` 以降を trim (無ければ空文字)
 *
 * `【効能】` が含まれない旧データ (AI スキップ等) では全文を prescription とし effect は空。
 */
export function splitActionIdea(actionIdea: string | null | undefined): {
  prescription: string;
  effect: string;
} {
  const raw = actionIdea ?? '';
  if (!raw) return { prescription: '', effect: '' };
  const marker = '【効能】';
  const idx = raw.indexOf(marker);
  if (idx < 0) {
    return {
      prescription: raw.replace('【処方・アクション】', '').trim(),
      effect: '',
    };
  }
  const prescription = raw.slice(0, idx).replace('【処方・アクション】', '').trim();
  const effect = raw.slice(idx + marker.length).trim();
  return { prescription, effect };
}

/** 重複した idea title に `_2`, `_3`, ... を自動付加 (旧 IdeaForm L446-452 踏襲)。 */
export function ensureUniqueIdeaTitle(title: string, existingTitles: string[]): string {
  const set = new Set(existingTitles);
  if (!set.has(title)) return title;
  let n = 2;
  while (set.has(`${title}_${n}`)) n += 1;
  return `${title}_${n}`;
}

/**
 * `aiReferences` を旧 v1 互換形式 (title/url/imageUrl/description) に変換するヘルパー。
 * 右ペイン参考事例 UI が `EcwIdeaReference` 風に表示できるようにする。
 */
export function aiReferenceToDisplay(r: AiReference): {
  id: string;
  title: string;
  body: string;
  url?: string;
  imageUrl?: string;
  polarity?: string;
} {
  return {
    id: r.id,
    title: r.title,
    body: r.body,
    url: r.url,
    polarity: r.polarity,
  };
}
